<script setup lang="ts">
import CourseCard from '../../components/CourseCard/CourseCard.vue';
import DateChip from 'shared/components/DateChip/DateChip.vue';
import { useRouter } from 'vue-router';
import { toFilterableItem, useUserCourseData } from '../../useUserCourseData';
import type { Filter } from '../../useUserCourseData';
import { listItemStatus } from 'shared/composables/useListFilters/useListFilters';
import BStatusChip from 'src/shared/components/base/BStatusChip/BStatusChip.vue';
import { mdiFileDocumentArrowRightOutline } from '@quasar/extras/mdi-v7';
import { useDate } from 'src/shared/composables/useDate';
import {
  computed,
  inject,
  onMounted,
  onUnmounted,
  ref,
  watch,
  watchEffect,
} from 'vue';
import { useQuasar } from 'quasar';
import { t } from 'src/boot/i18n';
import { useGlobalStore } from 'src/stores/global';
import type { Ref } from 'vue';
import type { UserRole } from 'src/types/User';
import BButton from 'src/shared/components/base/BButton/BButton.vue';
import ContentEmptyState from 'src/shared/components/ContentEmptyState/ContentEmptyState.vue';
import BListFilterBar from 'src/shared/components/base/BListFilterBar/BListFilterBar.vue';
import { useListFilters } from 'shared/composables/useListFilters/useListFilters';
import { hasDisplayableCourseScore } from '../../utilities/courseProgressState';
import { isCoursePaused } from '../../utilities/courseVisibility';
import { shouldStopCourseListInfiniteScroll } from './utilities/courseListInfiniteScroll';
import type { QInfiniteScroll } from 'quasar';

const $q = useQuasar();

interface Props {
  filter?: Filter;
}

const props = defineProps<Props>();

const { strapiDate } = useDate();

const {
  criteria,
  activeFilterCount,
  hideCompleted,
  sortBy,
  search,
  selectedStatuses,
  toggleStatus,
  clearAll,
} = useListFilters();

const {
  userCourses,
  baseUserCourses,
  getUserCourses,
  isLastUserCourse,
  isLastFilteredUserCourse,
  userCourseTotal,
  favoriteCourse,
  getCourseCardHeaderBg,
  courseCardHeaderBgData,
  removeUserCourse,
  courseProgressPercent,
  isLoading,
} = useUserCourseData({
  filter: props?.filter,
  criteria,
});

const criteriaActive = computed(
  () => activeFilterCount.value > 0 || hideCompleted.value,
);

const infiniteScrollRef = ref<QInfiniteScroll | null>(null);

// Criteria changes repaginate server-side: the mirrored repo query already
// updated the visible list, this refetch fills page 1 of the new ordering and
// re-arms the infinite scroll for the rest. Page-level filter tabs handle
// their own fetching and are covered by the client mirror alone.
watch(criteria, () => {
  if (props.filter?.key) return;
  void getUserCourses().then(() => {
    infiniteScrollRef.value?.reset();
    infiniteScrollRef.value?.resume();
  });
});

const router = useRouter();
const invalidLicense = inject<Ref<boolean>>('invalidLicense');
const currentUserRole = inject<Ref<UserRole | undefined>>('currentUserRole');
const isSchoolStudent = computed(() => currentUserRole?.value === 'student');

const globalStore = useGlobalStore();

function isCourseLocked(
  userCourse: { course: { is_free?: boolean } | null } | null | undefined,
): boolean {
  return !!invalidLicense?.value && !userCourse?.course?.is_free;
}

function isCoursePausedForCurrentRole(
  userCourse:
    | {
        is_assigned?: boolean;
        start_date?: string | null;
        completion_date?: string | null;
        time_accumulator?: number | null;
        score?: number | null;
        progress?: { sections?: unknown[] } | null;
      }
    | null
    | undefined,
) {
  return isSchoolStudent.value && isCoursePaused(userCourse);
}

// Empty list (once loading settles): explain why it's empty and what to do next.
const showEmptyState = computed(
  () => !isLoading.value && !userCourses.value.length,
);

// Criteria emptied a non-empty list — say so instead of "you have no courses".
const showFilteredEmptyState = computed(
  () =>
    showEmptyState.value &&
    criteriaActive.value &&
    baseUserCourses.value.length > 0,
);

watchEffect(() => {
  globalStore.setTotal(userCourses.value.length);
});
onMounted(async () => {
  globalStore.setTotal(userCourses.value.length);
  if (courseCardHeaderBgData.value?.url) return;
  await getCourseCardHeaderBg();
});

onUnmounted(() => {
  globalStore.setTotal(undefined);
});

const handleSelected = (userCourseDocId: string) => {
  const selectedCourse = userCourses.value.find(
    (userCourse) => userCourse.documentId === userCourseDocId,
  );
  if (selectedCourse && isCoursePausedForCurrentRole(selectedCourse)) {
    $q.notify({
      type: 'warning',
      message: t('SCHOOL_UNASSIGNED_PROGRESS_PAUSED_MESSAGE'),
    });
    return;
  }

  if (selectedCourse && isCourseLocked(selectedCourse)) {
    $q.notify({
      type: 'warning',
      message: t('LICENSE_REQUIRED_FOR_PREMIUM'),
    });
    return;
  }

  void router.push({ name: 'course.sections', params: { userCourseDocId } });
};

const handleToggleFavorite = async (
  userCourseDocId: string,
  isFavorite: boolean,
) => {
  await favoriteCourse(userCourseDocId, !isFavorite);
};

const handleRemove = (userCourseDocId: string) => {
  // Confirm dialog
  $q.dialog({
    title: t('CONFIRM'),
    message: t('ARE_YOU_SURE_REMOVE_USER_COURSE'),
    cancel: true,
    persistent: true,
    dark: true,
  })
    .onOk(() => {
      removeUserCourse(userCourseDocId)
        .then(() => {
          $q.notify({
            type: 'positive',
            message: t('COURSE_REMOVED_SUCCESSFULLY'),
          });
        })
        .catch(() => {
          $q.notify({
            type: 'negative',
            message: t('ERROR_REMOVING_COURSE'),
          });
        });
    })
    .onCancel(() => false);
};

async function onLoad(
  index: number,
  done: (stop: boolean) => void,
): Promise<void> {
  void index;

  await getUserCourses();
  globalStore.setTotal(userCourses.value.length);
  done(
    shouldStopCourseListInfiniteScroll(
      props.filter?.key,
      isLastUserCourse.value,
      {
        active: criteriaActive.value,
        isLastFilteredUserCourse: isLastFilteredUserCourse.value,
      },
    ),
  );
}
</script>

<template>
  <section>
    <div class="full-height overflow-auto">
      <BListFilterBar
        v-model:hide-completed="hideCompleted"
        v-model:sort-by="sortBy"
        v-model:search="search"
        :selected-statuses="selectedStatuses"
        :active-filter-count="activeFilterCount"
        :shown-count="userCourses.length"
        :total-count="
          props.filter?.key
            ? baseUserCourses.length
            : (userCourseTotal ?? baseUserCourses.length)
        "
        @toggle:status="toggleStatus"
        @clear="clearAll"
      />
      <q-banner
        v-if="showFilteredEmptyState"
        class="text-center q-mt-lg bg-transparent"
      >
        <p class="q-mb-sm">{{ $t('NO_TASKS_MATCH_FILTERS') }}</p>
        <q-btn
          flat
          no-caps
          color="accent"
          :label="$t('CLEAR_ALL')"
          @click="clearAll"
        />
      </q-banner>
      <div
        v-else-if="
          showEmptyState &&
          (props.filter?.key === 'is_favorite' ||
            props.filter?.key === 'is_favorite_all')
        "
      >
        <q-banner class="text-center q-mt-lg">
          <p class="title">{{ $t('NO_FAVORITES_AVAILABLE') }}</p>
        </q-banner>
      </div>
      <ContentEmptyState
        v-else-if="showEmptyState"
        content-type="course"
      />
      <q-infinite-scroll
        v-else
        ref="infiniteScrollRef"
        :offset="150"
        @load="onLoad"
      >
        <div
          class="row wrap items-start q-row-gutter-md q-col-gutter-md q-pb-xl q-mt-xs"
        >
          <div
            v-for="userCourse in userCourses"
            :key="userCourse.id"
            class="col-12 col-sm-6 col-md-4 col-lg-3 row justify-center"
          >
            <CourseCard
              v-if="userCourse?.id"
              class="my-card shadow-2 fit column justify-between"
              tag="article"
              style="
                min-height: 260px;
                max-width: 340px;
                border-radius: 18px;
                overflow: hidden;
              "
              bordered
              :image="
                userCourse?.course?.image?.url ?? courseCardHeaderBgData?.url
              "
              :name="userCourse?.course?.course_name"
              :subtitle="userCourse?.course?.subtitle"
              :is-free="userCourse?.course?.is_free"
              :has-favorite="true"
              :time-accumulator="userCourse?.time_accumulator"
              :score="
                hasDisplayableCourseScore(userCourse)
                  ? (userCourse?.score ?? undefined)
                  : undefined
              "
              :action-button-icon="mdiFileDocumentArrowRightOutline"
              :is-remove="true"
              :is-favorite="userCourse?.is_favorite ?? false"
              @action:click="handleSelected(userCourse?.documentId ?? '')"
              @toggle:favorite="
                handleToggleFavorite(
                  userCourse?.documentId ?? '',
                  userCourse?.is_favorite ?? false,
                )
              "
              @action:remove="handleRemove(userCourse?.documentId ?? '')"
            >
              <template #action-button>
                <BButton
                  :label="
                    isCoursePausedForCurrentRole(userCourse)
                      ? $t('SCHOOL_PROGRESS_PAUSED_SHORT')
                      : isCourseLocked(userCourse)
                        ? $t('LOCKED_PREMIUM_COURSE')
                        : $t('GO_TO_MY_COURSE')
                  "
                  text-color="dark"
                  class="text-weight-bolder full-width"
                  color="secondary"
                  size="custom"
                  :disable="
                    isCourseLocked(userCourse) ||
                    isCoursePausedForCurrentRole(userCourse)
                  "
                  @click.stop="handleSelected(userCourse?.documentId ?? '')"
                />
                <div
                  v-if="isCoursePausedForCurrentRole(userCourse)"
                  class="text-caption text-warning text-center q-mt-xs"
                >
                  {{ $t('SCHOOL_UNASSIGNED_PROGRESS_PAUSED_MESSAGE') }}
                </div>
              </template>
              <template #card-header-right>
                <BStatusChip
                  class="q-mt-sm"
                  :status="listItemStatus(toFilterableItem(userCourse))"
                />
                <DateChip
                  dense
                  color="white"
                  class="q-mr-sm q-mt-sm"
                >
                  {{
                    strapiDate(userCourse?.start_date ?? '', 'short') ??
                    $t('NOT_STARTED')
                  }}
                </DateChip>
              </template>
              <template #progress>
                <q-linear-progress
                  stripe
                  size="1.25em"
                  :value="courseProgressPercent(userCourse)?.percent ?? 0"
                >
                  <div class="absolute-full flex flex-center">
                    <q-badge
                      color="white"
                      text-color="primary"
                      :label="`${courseProgressPercent(userCourse)?.completedSections ?? 0}/${courseProgressPercent(userCourse)?.totalSections ?? 0}`"
                    />
                  </div>
                </q-linear-progress>
              </template>
            </CourseCard>
          </div>
        </div>
        <template #loading>
          <div class="row justify-center q-pa-md">
            <q-spinner
              size="40px"
              color="primary"
            />
          </div>
        </template>
      </q-infinite-scroll>
    </div>
  </section>
</template>
